import { CommonModule, ViewportScroller } from '@angular/common';
import {
    AfterContentChecked,
    AfterViewInit,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ElementRef,
    HostListener,
    Input,
    NgModule,
    OnDestroy,
    OnInit,
    QueryList,
    ViewChild,
    ViewChildren,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { Cms, PAGE_TYPES } from '@impact/data';
import { TranslateModule } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

import { carImageFallbackIcon } from '../../../icons';
import { GlobalDataService } from '../../core/global-data.service';
import { HeaderService } from '../../core/header.service';
import { SettingsService } from '../../core/settings.service';
import { DealershipsModule } from '../../dealerships/dealerships.module';
import { MapsLocation, MapView } from '../../maps/maps.component';
import { MapsModule } from '../../maps/maps.module';
import { DealershipDepartments, DealershipPageSections } from '../../models/dealerships';
import { RichTextModule } from '../../rich-text/rich-text.module';
import { SectionHeaderModule } from '../../section-header/section-header.module';
import { SpotsUiModule } from '../../spots/spots-ui.module';
import { UsedCarsModule } from '../../used-cars/used-cars.module';
import { FeatureDetectionService } from '../../utils/helpers/feature-detection.service';
import { UtilsModule } from '../../utils/utils.module';
import { carouselSpotData } from '../../core/helpers';

@Component({
    selector: 'app-dealership-page',
    template: `
        <main>
            <!-- TOP BANNER -->
            <app-carousel-spot *ngIf="data?.showTopBanner && topBannerData"
                [ngClass]="{'small-banner': data?.displaySmallBanner}"
                [data]="topBannerData"
                [lazyLoad]="true">
            </app-carousel-spot>

            <!-- PAGE NAVIGATION -->
            <div
                #stickyScrollRef
                class="scroll-sticky-wrapper"
                [ngClass]="{ 'scroll-sticky-wrapper--is-sticky': isFixed }"
                [ngStyle]="{ height: pageHeaderElementHeight }"
            >
                <div class="page-navigation-header dealership-page__page-navigation-header" #pageHeaderRef [style.top]="moveMenuDown ? headerHeight + 'px' : ''">
                    <div class="page-navigation-header__wrapper">
                        <div class="page-navigation-header__column-left">
                            <div class="dealership-page-header__info-wrapper">
                                <div class="dealership-page-header__info-name">{{ dealership?.displayName }}</div>
                                <a
                                    [href]="'tel:' + dealership?.contactPhoneNumber"
                                    class="dealership-page-header__info-contact"
                                    *ngIf="dealership?.contactPhoneNumber"
                                >
                                    Tlf: {{ dealership?.contactPhoneNumber | phonenumber }}
                                </a>
                            </div>
                        </div>

                        <div class="page-navigation-header__column-center">
                            <nav class="dealership-page-menu">
                                <ul class="page-secondary-navigation__list" #listElements>
                                    <li class="page-secondary-navigation__item" #listElement>
                                        <button
                                            class="button--minimal page-secondary-navigation__item-link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === DealershipPageSections.INFO }"
                                            (click)="scrollToSection(DealershipPageSections.INFO)"
                                        >
                                            <span class="page-secondary-navigation__item-text">{{
                                                'dealerships.dealership_page_navigation_item_info' | translate
                                            }}</span>
                                        </button>
                                    </li>
                                    <li class="page-secondary-navigation__item" #listElement>
                                        <button
                                            class="button--minimal page-secondary-navigation__item-link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === DealershipPageSections.OPENING_HOURS }"
                                            (click)="scrollToSection(DealershipPageSections.OPENING_HOURS)"
                                        >
                                            <span class="page-secondary-navigation__item-text">{{
                                                'dealerships.dealership_page_navigation_item_opening_hours' | translate
                                            }}</span>
                                        </button>
                                    </li>
                                    <li class="page-secondary-navigation__item" #listElement>
                                        <button
                                            class="button--minimal page-secondary-navigation__item-link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === DealershipPageSections.EMPLOYEES }"
                                            (click)="scrollToSection(DealershipPageSections.EMPLOYEES)"
                                        >
                                            <span class="page-secondary-navigation__item-text">{{
                                                'dealerships.dealership_page_navigation_item_employees' | translate
                                            }}</span>
                                        </button>
                                    </li>
                                    <li class="page-secondary-navigation__item" #listElement>
                                        <button
                                            class="button--minimal page-secondary-navigation__item-link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === DealershipPageSections.MAP }"
                                            (click)="scrollToSection(DealershipPageSections.MAP)"
                                        >
                                            <span class="page-secondary-navigation__item-text">{{
                                                'dealerships.dealership_page_navigation_item_map' | translate
                                            }}</span>
                                        </button>
                                    </li>
                                    <li class="page-secondary-navigation__item" #listElement *ngIf="data?.usedCarsQuery">
                                        <button
                                            class="button--minimal page-secondary-navigation__item-link"
                                            [ngClass]="{ 'is-active': activeScrollMenuItem === DealershipPageSections.CARS }"
                                            (click)="scrollToSection(DealershipPageSections.CARS)"
                                        >
                                            <span class="page-secondary-navigation__item-text">{{
                                                'dealerships.dealership_page_navigation_item_cars' | translate
                                            }}</span>
                                        </button>
                                    </li>
                                </ul>
                            </nav>
                        </div>

                        <div class="page-navigation-header__column-right">
                            <a [routerLink]="bookWorkshopPageUrl" *ngIf="bookWorkshopPageUrl" class="button--primary">
                                {{ 'dealerships.book_workshop_button_text' | translate }}
                            </a>
                        </div>
                    </div>
                </div>
            </div>

            <!-- INFO -->
            <section class="dealership-page-section" [id]="DealershipPageSections.INFO" #infoRef>
                <article class="dealership-info layout-wrapper">
                    <app-section-header
                        [headerCategoryText]="'dealerships.about_section_category_header' | translate"
                        [headerTextPrimary]="data.dealership.aboutDealershipTitle"
                        [isCentered]="true"
                    >
                    </app-section-header>
                    <app-rich-text class="dealership-info__text" [html]="data?.dealership?.aboutDealershipText"></app-rich-text>
                </article>
            </section>

            <!-- OPENING HOURS -->
            <section class="dealership-page-section" [id]="DealershipPageSections.OPENING_HOURS" #openingHoursRef>
                <article class="dealership__departments layout-wrapper">
                    <app-section-header
                        [headerCategoryText]="'dealerships.opening_hours_section_category_header' | translate"
                        [headerTextPrimary]="'dealerships.opening_hours_section_header' | translate"
                        [isCentered]="true"
                    >
                    </app-section-header>

                    <div class="dealership__department-opening-hours-wrapper">
                        <div class="dealership__department-opening-hours" *ngFor="let department of dealership?.departments">
                            <header class="dealership__opening-hours-header">
                                <div>
                                    <h2 class="h5 header-text">{{ department?.departmentName }}</h2>
                                    <div class="dealership-department-opening-hours-today">
                                        <ng-container *ngIf="department?.nextSevenDays?.[0]?.hoursOfBusiness?.[0]">
                                            {{ 'dealerships.open_today_text' | translate }}
                                            {{ department?.nextSevenDays?.[0]?.hoursOfBusiness?.[0]?.opensAt | date: 'HH:mm' }}
                                            -
                                            {{ department?.nextSevenDays?.[0]?.hoursOfBusiness?.[0]?.closesAt | date: 'HH:mm' }}
                                        </ng-container>
                                    </div>
                                </div>
                                <div class="campaign-tag" [ngClass]="{ 'closed-now': !isOpenNow(department) }">
                                    <ng-container *ngIf="isOpenNow(department); else closed">
                                        {{ 'dealerships.dealership_open_text' | translate }}
                                    </ng-container>
                                    <ng-template #closed>
                                        {{ 'dealerships.dealership_closed_text' | translate }}
                                    </ng-template>
                                </div>
                            </header>
                            <app-opening-hours [data]="department"></app-opening-hours>
                        </div>
                    </div>
                </article>
            </section>

            <!-- EMPLOYEES -->
            <section class="dealership-page-section" [id]="DealershipPageSections.EMPLOYEES" #employeesRef>
                <article class="dealership-employees layout-wrapper">
                    <app-section-header
                        [headerCategoryText]="'dealerships.employees_section_category_header' | translate"
                        [headerTextPrimary]="'dealerships.employees_section_header' | translate"
                        [introText]="'dealerships.employees_section_intro_text' | translate"
                        [isCentered]="true"
                    >
                    </app-section-header>

                    <div class="dealership-employees__overview">
                        <ng-container *ngIf="employeesByDepartment?.sales.length">
                            <h4 class="dealership-employees__section-header">{{ 'dealerships.department_name_sales' | translate }}</h4>
                            <ul class="dealership-employees__list">
                                <li class="employee-card" *ngFor="let employee of employeesByDepartment?.sales">
                                    <ng-container *ngTemplateOutlet="employeeCard; context: { item: employee }"></ng-container>
                                </li>
                            </ul>
                        </ng-container>
                        <ng-container *ngIf="employeesByDepartment?.autoshop.length">
                            <h4 class="dealership-employees__section-header">{{ 'dealerships.department_name_autoshop' | translate }}</h4>
                            <ul class="dealership-employees__list">
                                <li class="employee-card" *ngFor="let employee of employeesByDepartment?.autoshop">
                                    <ng-container *ngTemplateOutlet="employeeCard; context: { item: employee }"></ng-container>
                                </li>
                            </ul>
                        </ng-container>
                        <ng-container *ngIf="employeesByDepartment?.retail.length">
                            <h4 class="dealership-employees__section-header">{{ 'dealerships.department_name_retail' | translate }}</h4>
                            <ul class="dealership-employees__list">
                                <li class="employee-card" *ngFor="let employee of employeesByDepartment?.retail">
                                    <ng-container *ngTemplateOutlet="employeeCard; context: { item: employee }"></ng-container>
                                </li>
                            </ul>
                        </ng-container>
                        <ng-container *ngIf="employeesByDepartment?.damagecenter.length">
                            <h4 class="dealership-employees__section-header">{{ 'dealerships.department_name_damagecenter' | translate }}</h4>
                            <ul class="dealership-employees__list">
                                <li class="employee-card" *ngFor="let employee of employeesByDepartment?.damagecenter">
                                    <ng-container *ngTemplateOutlet="employeeCard; context: { item: employee }"></ng-container>
                                </li>
                            </ul>
                        </ng-container>
                    </div>
                </article>
            </section>

            <!-- Dealership map -->
            <section class="dealership-page-section" [id]="DealershipPageSections.MAP" #mapRef>
                <!-- <app-maps [mapView]="mapView.ONE_DEALERSHIP" [dealership]="dealership" [locations]="mapLocationsData"></app-maps> -->
                <app-maps [mapView]="mapView.ONE_DEALERSHIP" [locations]="mapLocationsData"></app-maps>
            </section>

            <!-- USED CARS -->
            <section *ngIf="data?.usedCarsQuery" class="dealership-page-section" [id]="DealershipPageSections.CARS" #carsRef>
                <div class="layout-wrapper">
                    <app-section-header
                        [headerTextPrimary]="
                            data?.usedCarsHeaderText
                                ? data?.usedCarsHeaderText
                                : ('dealerships.used_cars_spot_header' | translate)
                        "
                        [buttonLink]="data?.usedCarsHeaderLink"
                    >
                    </app-section-header>
                    <app-used-cars-list
                        [query]="data.usedCarsQuery"
                        [lastSlideText]="data.usedCarsLastSlideText"
                        [lastSlideLink]="data.usedCarsLastSlideLink"
                        [displayAmount]="data.usedCarsDisplayAmount"
                        [layoutMode]="data.usedCarsLayoutMode"
                    >
                    </app-used-cars-list>
                </div>
            </section>

            <section class="dealership-page-section" *ngIf="data?.mainGrid">
                <app-spot-grid [grid]="data?.mainGrid"></app-spot-grid>
            </section>
        </main>

        <!-- TEMPLATES -->
        <ng-template #employeeCard let-item="item">
            <div class="employee-card__image-wrapper">
                <img
                    *ngIf="item?.employeeImage; else imageFallback"
                    [src]="item?.employeeImage?.src | resolveMediaUrl: { width: 135, height: 135, format: 'webp' }"
                    [alt]="item?.employeeName.alt"
                    class="employee-card__image"
                    loading="lazy"
                    appImageLoadFadeIn
                />
            </div>
            <div class="employee-card__info">
                <h6 class="employee-card__name">{{ item?.employeeName }}</h6>
                <div class="employee-card__jobtitle">{{ item?.jobTitle }}</div>
                <div class="employee-card__phone">
                    <a href="tel:0045{{ item?.employeePhoneNumber }}">(+45) {{ item?.employeePhoneNumber | phonenumber }}</a>
                </div>
                <div class="employee-card__email">
                    <a href="mailto:{{ item?.employeeEmail }}">{{ item?.employeeEmail }}</a>
                </div>
            </div>
        </ng-template>

        <ng-template #imageFallback>
            <div class="employee-card__image-fallback">${carImageFallbackIcon}</div>
        </ng-template>
    `,
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DealershipPageComponent implements OnInit, AfterViewInit, AfterContentChecked, OnDestroy {
    public static ref = PAGE_TYPES.DEALERSHIP_PAGE;

    @ViewChild('stickyScrollRef') stickyScrollRef: ElementRef;
    @ViewChild('pageHeaderRef') pageHeaderRef: ElementRef;
    @ViewChildren('listElement') listElements: QueryList<ElementRef>;
    @ViewChild('infoRef') infoRef: ElementRef;
    @ViewChild('openingHoursRef') openingHoursRef: ElementRef;
    @ViewChild('employeesRef') employeesRef: ElementRef;
    @ViewChild('mapRef') mapRef: ElementRef;
    @ViewChild('carsRef') carsRef: ElementRef;

    // BND-1937
    // The generate types is not putting the url there but url is part of the response
    // instead of adding it to the dealership we added it here to make it work as expected
    @Input() data: Cms.DealershipPage & { url: string };

    topBannerData: Cms.CarouselSpot;
    dealershipDepartments = DealershipDepartments;
    mapView = MapView;
    dealership: Cms.DealershipDto;
    employeesByDepartment: any;
    bookWorkshopPageUrl: string;
    mapLocationsData: MapsLocation[] = [];
    private readonly unsubscribe = new Subject<void>();

    DealershipPageSections = DealershipPageSections;
    deactivateScrollTrigger: boolean;
    listElementsArray: any[];
    sectionsScrollOffset: number;
    stickyScrollElement: HTMLElement;
    stickyScrollElementYPosition: number;
    pageHeaderElement: HTMLElement;
    pageHeaderElementHeight: string;
    isFixed: boolean;
    moveMenuDown: boolean;
    headerHeight: number;
    prevScrollPosition: number;

    infoElementYPosition: number;
    infoElementHeight: number;

    openingHoursElementYPosition: number;
    openingHoursElementHeight: number;

    employeesElementYPosition: number;
    employeesElementHeight: number;

    mapElementYPosition: number;
    mapElementHeight: number;

    carsElementYPosition: number;
    carsElementHeight: number;

    activeScrollMenuItem: string | undefined;

    @HostListener('window:scroll')
    onWindowScroll() {
        if (this.featureDetectionService.isBrowser()) {
            if (this.deactivateScrollTrigger) {
                return;
            }

            // Info section
            if (window.scrollY >= this.infoElementYPosition - this.sectionsScrollOffset) {
                this.activeScrollMenuItem = DealershipPageSections.INFO;
            }

            // Opening Hours section
            if (window.scrollY >= this.elementScrollTriggerPosition(this.infoElementYPosition, this.infoElementHeight)) {
                this.activeScrollMenuItem = DealershipPageSections.OPENING_HOURS;
            }

            // Employees section
            if (window.scrollY > this.elementScrollTriggerPosition(this.openingHoursElementYPosition, this.openingHoursElementHeight)) {
                this.activeScrollMenuItem = DealershipPageSections.EMPLOYEES;
            }

            // Map section
            if (window.scrollY > this.elementScrollTriggerPosition(this.employeesElementYPosition, this.employeesElementHeight)) {
                this.activeScrollMenuItem = DealershipPageSections.MAP;
            }

            // Cars section
            if (window.scrollY > this.elementScrollTriggerPosition(this.mapElementYPosition, this.mapElementHeight)) {
                this.activeScrollMenuItem = DealershipPageSections.CARS;
            }

            // Below sections
            if (window.scrollY >= this.elementScrollTriggerPosition(this.carsElementYPosition, this.carsElementHeight)) {
                this.activeScrollMenuItem = undefined;
            }
        }
    }

    constructor(
        private cdr: ChangeDetectorRef,
        private scroller: ViewportScroller,
        private globaldataService: GlobalDataService,
        private settingsService: SettingsService,
        private featureDetectionService: FeatureDetectionService,
        private readonly headerService: HeaderService
    ) {
        this.featureDetectionService.onResize$.pipe(takeUntil(this.unsubscribe)).subscribe((viewport) => {
            if (this.stickyScrollRef?.nativeElement?.getBoundingClientRect) {
                // Set a potentially new Y position of component
                this.stickyScrollElementYPosition = this.stickyScrollRef.nativeElement.getBoundingClientRect().top + window.scrollY;
            }
            if (viewport && this.stickyScrollRef) {
                if (this.pageHeaderRef) {
                    this.pageHeaderElement = this.pageHeaderRef.nativeElement;
                    this.pageHeaderElementHeight = this.pageHeaderElement.clientHeight + 'px';
                }
            }
        });
    }

    ngOnInit(): void {

        // Map dealership page carousel data to correct interface
        if (this.data.carouselContentTop) {
            this.topBannerData = carouselSpotData(this.data.carouselContentTop);
        } else {
            this.data.showTopBanner = false;
        }

        this.settingsService
            .getGlobalPages()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((globalPages) => {
                this.bookWorkshopPageUrl = globalPages.bookWorkshopPage?.url ?? undefined;
            });

        if (this.featureDetectionService.isBrowser()) {
            this.sectionsScrollOffset = window.innerHeight / 2;
        }

        // Get formatted data from global data
        this.globaldataService
            .getDealerships()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((data) => {
                if (data.children) {
                    for (const item of data.children) {
                        if (this.data.dealership && item.id === this.data.dealership.id) {
                            this.dealership = item;
                            break;
                        }
                    }

                    if (this.dealership) {
                        this.mapLocationsData.push({
                            ...this.dealership,
                            pageUrl: this.data.url ?? ''
                        });

                        // Add employees by department
                        if (this.dealership?.employees?.length) {
                            this.employeesByDepartment = {
                                sales: [],
                                autoshop: [],
                                retail: [],
                                damagecenter: [],
                            };

                            for (const employee of this.dealership.employees) {
                                switch (employee.employeeDepartment) {
                                    case this.dealershipDepartments.SALES:
                                        this.employeesByDepartment.sales.push(employee);
                                        break;
                                    case this.dealershipDepartments.AUTOSHOP:
                                        this.employeesByDepartment.autoshop.push(employee);
                                        break;
                                    case this.dealershipDepartments.RETAIL:
                                        this.employeesByDepartment.retail.push(employee);
                                        break;
                                    case this.dealershipDepartments.DAMAGE:
                                        this.employeesByDepartment.damagecenter.push(employee);
                                        break;
                                    default:
                                        break;
                                }
                            }
                        }
                    }
                }

                this.cdr.markForCheck();
            });

        this.headerService
            .getScrollPosition()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((position: number) => {
                this.handlHeaderPosition(position);
            });

        this.headerService
            .getHeaderEvents()
            .pipe(takeUntil(this.unsubscribe))
            .subscribe((height: number) => {
                this.headerHeight = height;
                this.prevScrollPosition = height;
                this.cdr.markForCheck();
            });
    }

    handlHeaderPosition(position: number) {
        if (window.scrollY < this.stickyScrollElementYPosition - this.headerHeight || window.scrollY === 0) {
            this.isFixed = false;
            this.moveMenuDown = false;
            this.pageHeaderElementHeight = 'auto';
        } else {
            this.moveMenuDown = position < this.prevScrollPosition;
        }
        if (window.scrollY >= this.stickyScrollElementYPosition) {
            this.isFixed = true;
            this.pageHeaderElementHeight = `${this.stickyScrollElement.clientHeight}px`;
        }
        this.prevScrollPosition = position;
        this.cdr.markForCheck();
    }

    ngAfterViewInit() {
        if (this.listElements) {
            this.listElementsArray = this.listElements.toArray();
        }

        if (this.pageHeaderRef) {
            this.pageHeaderElement = this.pageHeaderRef.nativeElement;
            this.pageHeaderElementHeight = this.pageHeaderElement.clientHeight + 'px';

            this.scroller.setOffset([0, this.pageHeaderElement.clientHeight]);
        }
    }

    ngAfterContentChecked(): void {
        if (this.featureDetectionService.isBrowser()) {
            this.calculateSections();
        }
    }

    scrollToSection(sectionName: DealershipPageSections): void {
        this.deactivateScrollTrigger = true;
        const index = this.getActiveAnchorIndex(sectionName);
        this.scrollToListItem(index);
        this.scroller.scrollToAnchor(sectionName);
        this.activeScrollMenuItem = sectionName;

        const scrollerTimeout = setTimeout(() => {
            this.deactivateScrollTrigger = false;
            clearTimeout(scrollerTimeout);
            this.cdr.markForCheck();
        }, 500);
    }

    scrollToListItem(index: number): void {
        if (!this.listElements && index !== -1) {
            return;
        }

        if (this.featureDetectionService.isBrowser()) {
            const element: HTMLElement = this.listElementsArray[index].nativeElement;
            const parentElement = element.parentElement;

            if (parentElement) {
                let scrollDistance: number;

                if (index + 2 === this.listElementsArray.length) {
                    scrollDistance = parentElement.offsetWidth;
                } else {
                    scrollDistance = element.offsetLeft - parentElement?.scrollLeft - parentElement.offsetWidth / 2 + element.offsetWidth;
                }

                parentElement?.scrollBy({
                    top: 0,
                    left: scrollDistance,
                    behavior: 'smooth',
                });
            }
        }
    }

    getActiveAnchorIndex(sectionName: DealershipPageSections): number {
        switch (sectionName) {
            case DealershipPageSections.INFO:
                return 0;
            case DealershipPageSections.OPENING_HOURS:
                return 1;
            case DealershipPageSections.EMPLOYEES:
                return 2;
            case DealershipPageSections.MAP:
                return 3;
            case DealershipPageSections.CARS:
                return 4;
            default:
                return 0;
        }
    }

    calculateSections(): void {
        if (this.stickyScrollRef) {
            this.stickyScrollElement = this.stickyScrollRef.nativeElement;
            this.stickyScrollElementYPosition = this.elementYPosition(this.stickyScrollElement);
        }

        if (this.infoRef) {
            const infoElement = this.infoRef.nativeElement;
            this.infoElementYPosition = this.elementYPosition(infoElement);
            this.infoElementHeight = this.elementHeight(infoElement);
        }

        if (this.openingHoursRef) {
            const openingHoursElement = this.openingHoursRef.nativeElement;
            this.openingHoursElementYPosition = this.elementYPosition(openingHoursElement);
            this.openingHoursElementHeight = this.elementHeight(openingHoursElement);
        }

        if (this.employeesRef) {
            const employeesElement = this.employeesRef.nativeElement;
            this.employeesElementYPosition = this.elementYPosition(employeesElement);
            this.employeesElementHeight = this.elementHeight(employeesElement);
        }

        if (this.mapRef) {
            const mapElement = this.mapRef.nativeElement;
            this.mapElementYPosition = this.elementYPosition(mapElement);
            this.mapElementHeight = this.elementHeight(mapElement);
        }

        if (this.carsRef) {
            const carsElement = this.carsRef.nativeElement;
            this.carsElementYPosition = this.elementYPosition(carsElement);
            this.carsElementHeight = this.elementHeight(carsElement);
        }
    }

    elementScrollTriggerPosition(yPosition: number, height: number): number {
        return yPosition + height - this.sectionsScrollOffset;
    }

    elementYPosition(element: HTMLElement): number {
        return element.getBoundingClientRect().top + window.scrollY;
    }

    elementHeight(element: HTMLElement): number {
        return element.clientHeight > window.innerHeight / 2 ? element.clientHeight : window.innerHeight / 2;
    }

    // Set isOpenNow property on departments

    isOpenNow(department: Cms.DepartmentDto): boolean {
        const hoursOfBusiness = department?.nextSevenDays?.[0]?.hoursOfBusiness?.[0];
        if (hoursOfBusiness?.closesAt && hoursOfBusiness?.opensAt) {
            // Convert the ISO date strings to Date objects
            const opensAtTime = new Date(hoursOfBusiness.opensAt).getTime();
            const closesAtTime = new Date(hoursOfBusiness.closesAt).getTime();
            const nowTime = new Date().getTime();

            // Return true if current time is between open and close times
            return nowTime >= opensAtTime && nowTime < closesAtTime;
        } else {
            return false;
        }
    }

    ngOnDestroy(): void {
        this.unsubscribe.next();
        this.unsubscribe.complete();
    }
}

@NgModule({
    declarations: [DealershipPageComponent],
    imports: [
        TranslateModule.forChild(),
        SpotsUiModule,
        CommonModule,
        RouterModule,
        MapsModule,
        DealershipsModule,
        RichTextModule,
        UtilsModule,
        UsedCarsModule,
        SectionHeaderModule,
    ],
})
class DealershipPageModule {}

export const LazyComponent = DealershipPageComponent;
